import api from 'api';
import { get } from 'utils/fetch';
import { useState, useEffect, useContext } from 'react';
import { message, Spin, Input, Button } from 'antd';
import { PropertyInsertion } from 'pages/RecordInsertion/PropertyInsertion';
import { isPhoneSize } from 'utils/util';
import { GlobalContext } from 'GlobalContext';
import { LoanDetailed } from './LoanDetailed';
import { isAdmin, isSale } from 'utils/util';
import { USDollar, parsePgArray } from 'utils/format';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';

function TableHeader() {
    return (
        <div
            style={{
                display: 'flex',
                borderBottom: '1px solid black',
            }}
        >
            <HeaderCell width={60}></HeaderCell>
            <HeaderCell width={140}>Loan Status</HeaderCell>
            <HeaderCell width={200}>Outstanding Loan Amount</HeaderCell>
            <HeaderCell width={200}>Monthly Mortgage Payment</HeaderCell>
            <HeaderCell width={180}>Loan Prepay Restriction</HeaderCell>
            <HeaderCell width={180}>Loan Service Company</HeaderCell>
            <HeaderCell width={230}>Loan Type</HeaderCell>
            <HeaderCell width={180}>Rehab Loan Budget</HeaderCell>
            <HeaderCell width={180}>Released Rehab Loan</HeaderCell>
            <HeaderCell width={150}>Loan Amount</HeaderCell>
            <HeaderCell width={120}>Lender</HeaderCell>
            <HeaderCell width={100}>Rate</HeaderCell>
            <HeaderCell width={150}>Maturity Date</HeaderCell>
            <HeaderCell width={100}>Lien</HeaderCell>
            <HeaderCell width={100}>Impound</HeaderCell>
        </div>
    );
}

function HeaderCell({ width, children }) {
    return (
        <div
            style={{
                width: `${width}px`,
                minWidth: `${width}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                borderRight: '1px solid #ddd',
                flexShrink: 0,
            }}
        >
            {children}
        </div>
    );
}

function Th({ width, children }) {
    return (
        <th
            style={{
                width,
                minWidth: width,
                padding: '8px',
                borderRight: '1px solid #ddd',
                textAlign: 'center',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                background: '#f9f9f9',
            }}
        >
            {children}
        </th>
    );
}

function Td({ width, children }) {
    return (
        <td
            style={{
                width,
                minWidth: width,
                padding: '8px',
                borderRight: '1px solid #eee',
                textAlign: 'center',
                whiteSpace: 'nowrap',
            }}
        >
            {children}
        </td>
    );
}

function LoanTable({ loans }) {
    const { assetManager } = useContext(GlobalContext);
    const { setLoanId } = assetManager;

    return (
        <div
            style={{
                overflow: 'auto',
                maxHeight: 'calc(100vh - 80px)', // 👈 makes vertical scroll possible
                border: '1px solid #ddd',
                width: '100%',
            }}
        >
            <table
                style={{
                    borderCollapse: 'collapse',
                    width: 'max-content',
                    minWidth: '100%',
                }}
            >
                {/* Sticky header */}
                <thead
                    style={{
                        position: 'sticky',
                        top: 0,
                        background: 'white',
                        zIndex: 2,
                        borderBottom: '2px solid black',
                    }}
                >
                    <tr>
                        <Th width={60}>Edit</Th>
                        <Th width={140}>Loan Status</Th>
                        <Th width={200}>Outstanding Loan Amount</Th>
                        <Th width={200}>Monthly Mortgage Payment</Th>
                        <Th width={180}>Loan Prepay Restriction</Th>
                        <Th width={180}>Loan Service Company</Th>
                        <Th width={230}>Loan Type</Th>
                        <Th width={180}>Rehab Loan Budget</Th>
                        <Th width={180}>Released Rehab Loan</Th>
                        <Th width={100}>Lien</Th>
                        <Th width={100}>Impound</Th>
                        <Th width={150}>Loan Amount (1st)</Th>
                        <Th width={120}>Lender (1st)</Th>
                        <Th width={100}>Rate (1st)</Th>
                        <Th width={150}>Maturity Date (1st)</Th>

                        <Th width={150}>Loan Amount (2nd)</Th>
                        <Th width={120}>Lender (2nd)</Th>
                        <Th width={100}>Rate (2nd)</Th>
                        <Th width={150}>Maturity Date (2nd)</Th>

                        <Th width={150}>Loan Amount (3rd)</Th>
                        <Th width={120}>Lender (3rd)</Th>
                        <Th width={100}>Rate (3rd)</Th>
                        <Th width={150}>Maturity Date (3rd)</Th>

                        <Th width={150}>Loan Amount (4th)</Th>
                        <Th width={120}>Lender (4th)</Th>
                        <Th width={100}>Rate (4th)</Th>
                        <Th width={150}>Maturity Date (4th)</Th>
                    </tr>
                </thead>

                <tbody>
                    {loans
                        .sort((a, b) => a.id - b.id)
                        .map((loan) => (
                            <tr
                                key={loan.id}
                                style={{
                                    background:
                                        loan.loan_status === 'Closed'
                                            ? '#f9f9f9'
                                            : 'white',
                                }}
                            >
                                <Td width={60}>
                                    <Button
                                        type="link"
                                        icon={<EditOutlined />}
                                        onClick={() => setLoanId(loan.id)}
                                    />
                                </Td>
                                <Td width={140}>{loan.loan_status}</Td>
                                <Td width={200}>
                                    {USDollar.format(
                                        loan.outstanding_loan_amount
                                    )}
                                </Td>
                                <Td width={200}>
                                    {USDollar.format(
                                        loan.monthly_mortgage_payment
                                    )}
                                </Td>
                                <Td width={180}>
                                    {loan.loan_prepay_restriction}
                                </Td>
                                <Td width={180}>{loan.loan_service_company}</Td>
                                <Td width={230}>
                                    {parsePgArray(loan.loan_type).join(', ')}
                                </Td>
                                <Td width={180}>
                                    {USDollar.format(loan.rehab_loan_budget)}
                                </Td>
                                <Td width={180}>
                                    {USDollar.format(loan.released_rehab_loan)}
                                </Td>
                                <Td width={100}>{loan.lien}</Td>
                                <Td width={100}>
                                    {loan.impound ? 'Yes' : 'No'}
                                </Td>
                                <Td width={150}>
                                    {USDollar.format(loan.loan_amount)}
                                </Td>
                                <Td width={120}>{loan.lender}</Td>
                                <Td width={100}>{loan.rate}</Td>
                                <Td width={150}>{loan.maturity_date}</Td>

                                <Td width={150}>
                                    {USDollar.format(loan.loan_amount_2)}
                                </Td>
                                <Td width={120}>{loan.lender_2}</Td>
                                <Td width={100}>{loan.rate_2}</Td>
                                <Td width={150}>{loan.maturity_date_2}</Td>

                                <Td width={150}>
                                    {USDollar.format(loan.loan_amount_3)}
                                </Td>
                                <Td width={120}>{loan.lender_3}</Td>
                                <Td width={100}>{loan.rate_3}</Td>
                                <Td width={150}>{loan.maturity_date_3}</Td>

                                <Td width={150}>
                                    {USDollar.format(loan.loan_amount_4)}
                                </Td>
                                <Td width={120}>{loan.lender_4}</Td>
                                <Td width={100}>{loan.rate_4}</Td>
                                <Td width={150}>{loan.maturity_date_4}</Td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
    );
}

export function LoanInfo() {
    const {
        manageAssetId,
        setManageAssetId,
        accessToken,
        assetManager,
        refreshSession,
    } = useContext(GlobalContext);
    const { loanId, setLoanId } = assetManager;
    const [loans, setLoans] = useState([]);
    const [loading, setLoading] = useState(
        manageAssetId < 0 || loanId !== 0 ? false : true
    );
    const [messageApi, contextHolder] = message.useMessage();

    const displayErrorMessage = (message) => {
        messageApi.open({
            type: 'error',
            content: message,
        });
    };

    async function fetchLoans(assetId) {
        await get(
            api.asset.getLoans(assetId),
            setLoans,
            (errors) =>
                errors.forEach((err) => {
                    displayErrorMessage(err.message);
                }),
            accessToken
        );
        refreshSession();
        setLoading(false);
    }

    useEffect(() => {
        if (loanId === 0 && manageAssetId > 0) {
            setLoading(true);
            fetchLoans(manageAssetId);
        }
    }, [manageAssetId, loanId]);

    return (
        <>
            {loading && (
                <div style={{ marginTop: '30px', textAlign: 'center' }}>
                    <Spin />
                </div>
            )}
            {!loading && (
                <>
                    {loanId !== 0 && (
                        <div
                            style={
                                isPhoneSize()
                                    ? {
                                          height: 'calc(100vh - 310px)',
                                          overflowY: 'scroll',
                                          scrollbarWidth: 'none',
                                          overflowX: 'hidden',
                                      }
                                    : {
                                          height: 'calc(100vh - 30px)',
                                          overflowY: 'scroll',
                                          scrollbarWidth: 'none',
                                          overflowX: 'hidden',
                                      }
                            }
                        >
                            <LoanDetailed />
                        </div>
                    )}
                    {loanId === 0 && (
                        <>
                            {isAdmin() && (
                                <div style={{ marginTop: '10px' }}>
                                    <Button
                                        type="default"
                                        icon={<PlusOutlined />}
                                        onClick={() => setLoanId(-1)}
                                    >
                                        Add
                                    </Button>
                                </div>
                            )}
                            <LoanTable loans={loans} />
                        </>
                    )}
                </>
            )}
        </>
    );
}
